/**
 * Retry Logic for API Calls
 *
 * Provides exponential backoff retry for fetch requests.
 * Retries on:
 *   - Network errors (TypeError / failed to fetch)
 *   - 5xx server errors
 * Does NOT retry on:
 *   - 4xx client errors (including 401, 403, 404)
 *   - Successful responses
 *
 * Usage: wrap any fetch call with retryFetch() or use the
 * integrated retry in apiFetch/apiRawFetch via options.
 */

export interface RetryOptions {
  /** Maximum number of retry attempts (default: 2 = up to 3 total calls) */
  maxRetries?: number;
  /** Base delay in ms before first retry (default: 600) */
  baseDelay?: number;
  /** Maximum delay cap in ms (default: 8000) */
  maxDelay?: number;
  /** Jitter factor 0–1 (default: 0.3) — randomizes delay to avoid thundering herd */
  jitter?: number;
  /** If true, skip retry entirely (for time-sensitive requests) */
  noRetry?: boolean;
}

const DEFAULT_RETRY: Required<RetryOptions> = {
  maxRetries: 2,
  baseDelay: 200,  // was 800ms — first retry now at ~200ms instead of ~800ms
  maxDelay: 6000,
  jitter: 0.3,
  noRetry: false,
};

/** Check if an HTTP status code is retryable (5xx) */
function isRetryableStatus(status: number): boolean {
  return status >= 500 && status < 600;
}

/** Calculate delay with exponential backoff + jitter */
function calcDelay(attempt: number, base: number, max: number, jitter: number): number {
  const exp = base * Math.pow(2, attempt); // 600, 1200, 2400, ...
  const capped = Math.min(exp, max);
  const jitterAmount = capped * jitter * Math.random();
  return capped + jitterAmount;
}

/** Sleep for ms */
function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}

/**
 * Execute a fetch call with retry logic.
 *
 * @param fn - A function that performs the fetch and returns a Response.
 *             It will be called multiple times on retryable failures.
 * @param opts - Retry configuration
 * @returns The successful Response
 * @throws The last error if all retries are exhausted
 */
export async function retryFetch(
  fn: () => Promise<Response>,
  opts?: RetryOptions,
): Promise<Response> {
  const cfg = { ...DEFAULT_RETRY, ...opts };

  if (cfg.noRetry || cfg.maxRetries <= 0) {
    return fn();
  }

  let lastError: unknown;

  for (let attempt = 0; attempt <= cfg.maxRetries; attempt++) {
    try {
      const res = await fn();

      // If the response is OK or a non-retryable error (4xx), return it as-is
      if (res.ok || !isRetryableStatus(res.status)) {
        return res;
      }

      // 5xx — retryable (silent retry, no logging until final failure)
      if (attempt < cfg.maxRetries) {
        const delay = calcDelay(attempt, cfg.baseDelay, cfg.maxDelay, cfg.jitter);
        await sleep(delay);
        continue;
      }

      // Exhausted retries — return the 5xx response for normal error handling
      return res;
    } catch (err) {
      lastError = err;

      // Network error (TypeError: Failed to fetch) — retryable
      if (attempt < cfg.maxRetries) {
        const delay = calcDelay(attempt, cfg.baseDelay, cfg.maxDelay, cfg.jitter);
        await sleep(delay);
        continue;
      }
    }
  }

  // All retries exhausted with network errors — log once
  console.error(
    `[Retry] All ${cfg.maxRetries} retries exhausted due to network error.`,
    lastError,
  );
  throw lastError;
}